import React, { useState, useEffect } from "react";
import { Shield } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

// Icon Generator Function
const generateIcon = (index) => {
  const icons = [
    { emoji: "🏦", bg: "bg-blue-50", color: "text-blue-600" },
    { emoji: "💰", bg: "bg-green-50", color: "text-green-600" },
    { emoji: "🏢", bg: "bg-purple-50", color: "text-purple-600" },
    { emoji: "📄", bg: "bg-amber-50", color: "text-amber-600" },
    { emoji: "🛡️", bg: "bg-rose-50", color: "text-rose-600" },
    { emoji: "👥", bg: "bg-indigo-50", color: "text-indigo-600" },
    { emoji: "📊", bg: "bg-cyan-50", color: "text-cyan-600" },
    { emoji: "💳", bg: "bg-pink-50", color: "text-pink-600" },
    { emoji: "💵", bg: "bg-teal-50", color: "text-teal-600" },
    { emoji: "📈", bg: "bg-orange-50", color: "text-orange-600" },
  ];
  
  return icons[index % icons.length];
};

const Ucb = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [ucbData, setUcbData] = useState(null);

  const [showBanksList, setShowBanksList] = useState(false);
  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  useEffect(() => {
    fetchUcbData();
  }, []);

  const fetchUcbData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/ucb/get_ucb_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setUcbData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching UCB data:', error);
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: "Urban Cooperative Banks (UCBs)",
      overview: "Overview",
      coreActivities: "Core Activities",
      keyStats: "Key Statistics",
      servicesTitle: "Description of Services",
      policyTitle: "Policy Framework"
    },
    ta: {
      title: "நகர்ப்புற கூட்டுறவு வங்கிகள் (UCBs)",
      overview: "கண்ணோட்டம்",
      coreActivities: "முக்கிய நடவடிக்கைகள்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      servicesTitle: "சேவைகளின் விளக்கம்",
      policyTitle: "கொள்கை கட்டமைப்பு"
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!ucbData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No UCB data available</div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen py-8">
      {/* Header */}
      <h1 className="text-4xl font-bold text-center mt-6 mb-8 text-gray-900">
        {t.title}
      </h1>

      {/* Overview Section */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🏦</span>
            </div>
            {t.overview}
          </h2>
          <div className="border-l-4 border-blue-600 pl-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              {language === 'en' ? ucbData.overview_eng : ucbData.overview_tamil}
            </p>
          </div>
        </div>
      </div>

{/* Core Activities Section */}
{ucbData.core_activity && ucbData.core_activity.length > 0 && (
  <div className="max-w-7xl mx-auto px-6 mb-12">
    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
      {t.coreActivities}
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {ucbData.core_activity.map((activity, index) => {
        const iconStyle = generateIcon(index);
        
        return (
          <div
            key={index}
            className={`${iconStyle.bg} rounded-xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100`}
          >
            <div className="text-3xl mb-3">
              {iconStyle.emoji}
            </div>
            <p className="text-gray-800 text-base font-semibold leading-snug">
              {language === 'en' ? activity.content_eng : activity.content_tamil}
            </p>
          </div>
        );
      })}
    </div>
  </div>
)}
{/* Key Statistics */}
{ucbData.key_stats && ucbData.key_stats.length > 0 && (
  <div className="max-w-7xl mx-auto px-6 mb-12">
    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
      {t.keyStats}
    </h2>

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {ucbData.key_stats.map((stat, index) => {
        const iconStyle = generateIcon(index);
        
        return (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center border border-gray-100"
          >
            <div className="text-4xl mb-2">{iconStyle.emoji}</div>
            <div className="text-2xl font-bold text-blue-600 mb-1">
              <span className="text-3xl">
                {language === 'en' ? stat.heading_eng : stat.heading_tamil}
              </span>
            </div>
            <p className="text-xs text-gray-600 font-medium">
              {language === 'en' ? stat.content_eng : stat.content_tamil}
            </p>
          </div>
        );
      })}
    </div>
  </div>
)}
{/* UCB Banks List - Collapsible */}
<div className="max-w-7xl mx-auto px-6 mb-12">
  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
    {language === 'en' ? 'List of Urban Cooperative Banks' : 'நகர்ப்புற கூட்டுறவு வங்கிகள் பட்டியல்'}
  </h2>

  <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
    <button
      onClick={() => setShowBanksList(!showBanksList)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
    >
      <span className="text-lg font-semibold">
        {language === 'en' ? 'Click to view all UCB Banks' : 'அனைத்து UCB வங்கிகளைப் பார்க்க கிளிக் செய்யவும்'}
      </span>
      <svg
        className={`w-6 h-6 transition-transform duration-300 ${showBanksList ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    {showBanksList && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 animate-fadeIn">
        {(ucbData.banks_list || []).map((bank, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg hover:shadow-md transition-all border border-blue-100"
          >
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white font-bold text-xs">{index + 1}</span>
            </div>
            <span className="text-gray-800 text-sm font-medium leading-relaxed">
              {bank.name}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
</div>
      {/* Description of Services */}
      {ucbData.desc_of_service && ucbData.desc_of_service.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t.servicesTitle}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ucbData.desc_of_service.map((service, index) => {
              const borderColors = ['border-blue-600', 'border-green-600', 'border-purple-600', 'border-amber-600', 'border-rose-600'];
              const iconStyle = generateIcon(index);
              
              return (
                <div key={index} className={`bg-white rounded-xl p-6 shadow-md border-l-4 ${borderColors[index % borderColors.length]}`}>
                  <div className="text-3xl mb-3">{iconStyle.emoji}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {language === 'en' ? service.head_eng : service.head_tamil}
                  </h3>
                  <p className="text-gray-700">
                    {language === 'en' ? service.content_eng : service.content_tamil}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Policy Framework */}
      {ucbData.policy_frame_work && ucbData.policy_frame_work.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t.policyTitle}
          </h2>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <div className="space-y-6">
              {ucbData.policy_frame_work.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-blue-600 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {language === 'en' ? item.head_eng : item.head_tamil}
                    </h3>
                    <p className="text-gray-700">
                      {language === 'en' ? item.content_eng : item.content_tamil}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ucb;